import type { StaffMemoryCommandOutcome, StaffMemoryFact } from "@lafayette/schemas";
import { useState } from "react";

import type { MemoryCommand, MemoryCommandResult } from "../commands";
import { factValueLabel, kindLabel } from "../labels";
import { draftFromFact, type ValueDraft, validateValueDraft, valueFromDraft } from "../value-form";
import { MemoryDialog, type Reason, ReasonSelect, useCommandForm } from "./MemoryDialog";
import { ValueField } from "./ValueField";

type Props = Readonly<{
  fact: StaffMemoryFact;
  run: (request: MemoryCommand) => Promise<MemoryCommandResult>;
  onDone: (outcome: StaffMemoryCommandOutcome) => void;
  onClose: () => void;
}>;

/**
 * M-S11. A correction makes a NEW fact and moves the old one to history. The kind cannot change: a
 * correction never moves a fact to another kind (the server refuses it).
 */
export function CorrectDialog({ fact, run, onDone, onClose }: Props) {
  const [draft, setDraft] = useState<ValueDraft>(() => draftFromFact(fact));
  const [reason, setReason] = useState<Reason>("");
  const [errors, setErrors] = useState<{ value?: string; reason?: string }>({});
  const form = useCommandForm(onDone);

  function validate() {
    const next = {
      value: validateValueDraft(fact.kind, draft),
      reason: reason === "" ? "Choose a reason." : undefined,
    };
    setErrors({
      ...(next.value === undefined ? {} : { value: next.value }),
      ...(next.reason === undefined ? {} : { reason: next.reason }),
    });
    return next.value === undefined && reason !== "";
  }

  function submit() {
    if (reason === "") return;
    void form.submit(() =>
      run({
        command: "correct",
        factId: fact.id,
        body: {
          expectedVersion: fact.version,
          kind: fact.kind,
          value: valueFromDraft(fact.kind, draft),
          reasonCode: reason,
        },
      }),
    );
  }

  const invalid = form.failure?.invalidField ?? null;
  const fieldFailure = invalid === "value" || invalid === "reasonCode";

  return (
    <MemoryDialog
      title="Correct this memory"
      description="A correction adds the new value and moves the old value to history."
      confirmLabel="Save correction"
      failure={fieldFailure ? null : form.failure}
      pending={form.pending}
      validate={validate}
      onSubmit={submit}
      onClose={onClose}
    >
      <div className="grid justify-items-start gap-1">
        <p className="m-0 text-sm text-muted-foreground">Now: {factValueLabel(fact)}</p>
        <p className="m-0 text-sm text-muted-foreground">
          Kind: {kindLabel(fact.kind)} (a correction keeps the kind)
        </p>
      </div>
      <ValueField
        idPrefix="memory-correct"
        kind={fact.kind}
        draft={draft}
        onChange={setDraft}
        error={invalid === "value" ? form.failure?.message : errors.value}
      />
      <ReasonSelect
        value={reason}
        onChange={setReason}
        error={invalid === "reasonCode" ? form.failure?.message : errors.reason}
      />
    </MemoryDialog>
  );
}
